// Getting a report on screen before it is asked for.
//
// A report opened from the sidebar used to wait on three things in a row: the
// page's code chunk (fetched on click), then the page rendering, then its
// query. Each is a round trip, and the query is the slow one (a report nobody
// has opened this session takes the server 0.5-1.5s). So the moment intent is
// known, all of it starts at once:
//   - a sidebar item under the pointer (or keyboard focus) fetches the chunk
//     at once and, after a short dwell, the report's primary query for the
//     window the page will open on;
//   - a fresh load starts the page's chunk and query before React renders,
//     alongside the session check (bootPrefetch, called from index.js);
//   - once the first page has settled, the reports this person opens most are
//     warmed in idle time (warmFavourites).
// Every prefetch is a GET the page itself will make, under the key it will
// read (lib/report-query holds both halves), so the page mounts onto a
// request already in flight or an answer already cached. Never an export.
import { queryClient } from '@/providers/query-provider';
import { initialWindow, liveWindow, resolveWindow, windowWasChosen } from '@/providers/report-window';
import { orgSettingsQuery } from '@/hooks/use-settings';
import { ordersQuery, attributionQuery, ORDERS_PAGE_ROWS } from '@/hooks/use-commerce';
import { roleHome } from '@/lib/permissions';
import { preloadDataStart } from '@/hooks/use-data-start';
import {
  commandCenterQuery, weekMovedQuery, budgetPlanQuery, growthActionsQuery, storeInsightsQuery,
  customerValueQuery, purchasesQuery, placesQuery, campaignsQuery, leaksQuery, channelRolesQuery,
  funnelsQuery, studioQuery, insightQuery,
} from '@/lib/report-query';

// The import() paths are the same modules App.js lazy-loads, so webpack
// serves one chunk for both. Pages missing from this map load on click.
const LOADERS = {
  // Home is Command Center for a store, the lead dashboard for everyone else.
  '/dashboard': () => (isStore() ? import('@/pages/growth-command-center-v2') : import('@/Dashboard')),
  '/leads': () => import('@/pages/leads-kanban'),
  '/pipelines': () => import('@/pages/pipelines-page'),
  '/contacts': () => import('@/pages/contacts-page'),
  '/follow-ups': () => import('@/pages/followups-page'),
  '/meetings': () => import('@/pages/meetings-page'),
  '/tasks': () => import('@/pages/tasks-page'),
  '/whatsapp': () => import('@/pages/whatsapp-inbox-page'),
  '/tracking/links': () => import('@/pages/links-page'),
  '/growth/guide': () => import('@/pages/growth-guide'),
  '/growth/actions': () => import('@/pages/growth-actions'),
  '/growth/findings': () => import('@/pages/growth-findings'),
  '/growth/customer-value': () => import('@/pages/growth-customer-value'),
  '/growth/buyers': () => import('@/pages/growth-buyers'),
  '/growth/campaigns': () => import('@/pages/growth-campaigns'),
  '/growth/audience-builder': () => import('@/pages/growth-audience-builder'),
  '/growth/leaks': () => import('@/pages/growth-leaks'),
  '/studio': () => import('@/pages/signal-studio'),
  '/sale-season': () => import('@/pages/sale-season'),
  '/growth/channel-roles': () => import('@/pages/growth-channel-roles'),
  '/growth/funnels': () => import('@/pages/growth-funnels'),
  '/growth/creatives': () => import('@/pages/growth-creatives'),
  '/commerce': () => import('@/pages/commerce-orders'),
  '/commerce/customers': () => import('@/pages/commerce-customers'),
  '/commerce/abandoned': () => import('@/pages/commerce-abandoned'),
  '/commerce/recovery': () => import('@/pages/commerce-recovery'),
  '/commerce/recommendations': () => import('@/pages/commerce-recs'),
  '/commerce/recommendations/rules': () => import('@/pages/commerce-recs-rules'),
  '/commerce/attribution': () => import('@/pages/commerce-attribution'),
  '/commerce/insights': () => import('@/pages/commerce-insights'),
  '/commerce/segments': () => import('@/pages/commerce-segments'),
  '/commerce/audiences': () => import('@/pages/audience-lists'),
  '/commerce/products': () => import('@/pages/commerce-products'),
  '/commerce/costs': () => import('@/pages/commerce-costs'),
  '/signal-health': () => import('@/pages/commerce-dashboard'),
  '/number-check': () => import('@/pages/number-check'),
  '/value-report': () => import('@/pages/value-report'),
  '/tracking/stream': () => import('@/pages/event-stream'),
  '/tracking/health': () => import('@/pages/event-health-page'),
  '/tracking/email': () => import('@/pages/email-analytics-page'),
  '/tracking/attribution': () => import('@/pages/attribution-page'),
  '/tracking/site-scan': () => import('@/pages/site-scan'),
  '/server-container': () => import('@/pages/server-container'),
  '/automations': () => import('@/pages/automations-page'),
  '/settings': () => import('@/pages/settings-page'),
  '/integrations': () => import('@/pages/integrations-page'),
  '/setup': () => import('@/pages/setup'),
};

// What each report asks for first. `supports` is the page's own
// useReportWindow(...) argument, so the window resolves to the days the page
// will request; `queries(w, params, path)` returns the page's own query
// definitions. Every entry is an e-commerce report.
const INSIGHT_KIND = { product: 'product', channels: 'channel-value', funnel: 'funnel', identity: 'identity' };
const DATA = {
  '/dashboard': {
    supports: { prefer: 30 },
    queries: (w, sp) => {
      const wk = sp.get('wk') || 'calendar';
      return [commandCenterQuery(w.effectiveDays), weekMovedQuery(wk), weekMovedQuery('rolling'),
        weekMovedQuery('calendar'), budgetPlanQuery(wk, '')];
    },
  },
  '/growth/actions': { queries: () => [growthActionsQuery()] },
  '/growth/findings': { queries: (w) => [storeInsightsQuery(w.effectiveDays)] },
  '/growth/customer-value': { queries: () => [customerValueQuery()] },
  '/growth/buyers': { supports: { min: 7, max: 365 }, queries: (w) => [purchasesQuery(w.effectiveDays), placesQuery(w.effectiveDays)] },
  '/growth/campaigns': { supports: { min: 1, max: 365 }, queries: (w, sp) => [campaignsQuery(w.effectiveDays, sp.get('level') || 'campaign', w.range)] },
  '/growth/leaks': { queries: (w) => [leaksQuery(w.effectiveDays)] },
  '/growth/channel-roles': { queries: (w) => [channelRolesQuery(w.effectiveDays)] },
  '/growth/funnels': { queries: (w) => [funnelsQuery(w.effectiveDays)] },
  '/studio': { supports: { max: 90 }, queries: (w) => [studioQuery(Number(w.effectiveDays) || 30)] },
  '/commerce': {
    supports: { allowAll: true, live: true },
    queries: (w, sp) => [ordersQuery(ORDERS_PAGE_ROWS, null, null, { days: w.days, range: w.range }, sp.get('placement') || 'web', null,
      sp.get('store') && sp.get('store') !== 'all' ? sp.get('store') : null)],
  },
  '/commerce/attribution': {
    // A deep link to another credit model reads that model; only the default is warmed.
    queries: (w, sp) => (!sp.get('model') || sp.get('model') === 'compare' || sp.get('model') === 'last_click'
      ? [attributionQuery(w.days, 'last_click', w.range)] : []),
  },
  '/commerce/insights': {
    queries: (w, sp, path) => {
      const kind = INSIGHT_KIND[path.split('/')[3] || 'product'];
      return kind ? [insightQuery(kind, { days: w.days, range: w.range, model: 'last_click', segment: 'all' })] : [];
    },
  },
};

const closest = (map, path) => {
  let key = path;
  while (key && !map[key]) key = key.replace(/\/[^/]*$/, '');
  return key ? [key, map[key]] : [null, null];
};
// A report's data is its own: /commerce/customers must not warm /commerce's
// orders. Only Analytics & Insights keeps its tab in the path.
const dataFor = (path) => (DATA[path] ? [path, DATA[path]]
  : path.startsWith('/commerce/insights/') ? ['/commerce/insights', DATA['/commerce/insights']] : [null, null]);
const split = (to) => {
  const [path, qs = ''] = String(to || '').split('?');
  return [path === '/' || !path ? roleHome() : path, new URLSearchParams(qs)];
};

// Whether this workspace is a store. Only a store has these reports; asking a
// lead workspace's server for its Command Center is a wasted request. Known
// from the settings once they are in; before that (a fresh load), from what
// this browser saw last time.
const ECOM_HINT = 'mm.ecom-hint';
function isStore() {
  const s = queryClient.getQueryData(orgSettingsQuery.queryKey);
  if (s) return s?.values?.['features.ecommerce'] === true;
  try { return localStorage.getItem(ECOM_HINT) === '1'; } catch { return false; }
}
queryClient.getQueryCache().subscribe((e) => {
  if (e.type !== 'updated' || e.action?.type !== 'success' || e.query.queryKey[0] !== orgSettingsQuery.queryKey[0]) return;
  try { localStorage.setItem(ECOM_HINT, e.query.state.data?.values?.['features.ecommerce'] === true ? '1' : '0'); } catch { /* not remembered: the next load waits for the settings */ }
});

// The window the page will open on: the live one while the app is mounted,
// the URL + stored one on a fresh load (the same function the provider uses).
function currentWindow() {
  const live = liveWindow();
  if (live) return live;
  const sp = new URLSearchParams(window.location.search);
  return { ...initialWindow(sp), chosen: windowWasChosen(sp) };
}

const warmed = new Set();
function preloadChunk(path) {
  const [route, load] = closest(LOADERS, path);
  // Home's chunk depends on the kind of workspace, which a fresh load may
  // learn only after its first guess.
  const key = route === '/dashboard' ? `${route}:${isStore() ? 'store' : 'leads'}` : route;
  if (!load || warmed.has(key)) return;
  warmed.add(key);
  try { load().catch(() => warmed.delete(key)); } catch { warmed.delete(key); }
}

/** Start the report's primary queries for `to` (a path with its query string). */
export function prefetchRouteData(to) {
  const [path, sp] = split(to);
  const [, entry] = dataFor(path);
  if (!entry || !isStore()) return;
  const s = entry.supports || {};
  const win = currentWindow();
  // A page's preferred opening applies only while nobody has chosen a window
  // (report-window's adopt), so the prefetch borrows it under the same rule.
  const base = !win.chosen && s.prefer && !win.range ? { ...win, days: s.prefer } : win;
  const w = resolveWindow(base, s);
  for (const q of entry.queries(w, sp, path)) {
    // meta.prefetch: a failure nobody is looking at yet stays quiet; the page
    // that mounts onto it refetches and reports its own (query-provider).
    queryClient.prefetchQuery({ ...q, meta: { ...(q.meta || {}), prefetch: true } }).catch(() => {});
  }
}

/**
 * The sidebar's hover and focus. The chunk starts at once (it is cheap and
 * cached for good); the data waits for the pointer to rest ~90ms, so sweeping
 * down the rail does not ask the server for every report it crosses.
 */
export function preloadRoute(to, e) {
  const [path] = split(to);
  preloadChunk(path);
  if (e?.type === 'focus') { prefetchRouteData(to); return; }
  const t = setTimeout(() => prefetchRouteData(to), 90);
  const el = e?.currentTarget;
  if (el?.addEventListener) el.addEventListener('mouseleave', () => clearTimeout(t), { once: true });
}

/** A fresh load: start the page's chunk, its settings and its data before React renders. */
export function bootPrefetch() {
  try { if (!localStorage.getItem('token')) return; } catch { return; }
  const base = (document.querySelector('base')?.getAttribute('href') || '/app').replace(/\/$/, '');
  const { pathname, search } = window.location;
  if (!pathname.startsWith(base)) return;
  const path = pathname.slice(base.length) || '/';
  const to = `${path}${search}`;
  preloadChunk(split(to)[0]);
  // A report's date control reads the day recording began; ask for it now too.
  if (dataFor(split(to)[0])[0] && isStore()) preloadDataStart();
  // The shell needs these settings anyway; asking now saves a round trip. A
  // store that this browser has not seen before learns it here, then warms.
  queryClient.prefetchQuery(orgSettingsQuery).then(() => prefetchRouteData(to)).catch(() => {});
  prefetchRouteData(to);
}

// ── What this person opens most ─────────────────────────────────────────────
const OPENS = 'mm.report-opens';
const readOpens = () => { try { return JSON.parse(localStorage.getItem(OPENS) || '{}') || {}; } catch { return {}; } };

/** Count an opened report (by its route, not its query string). */
export function noteOpen(pathname) {
  const [key] = dataFor(pathname);
  if (!key) return;
  const o = readOpens();
  o[key] = (o[key] || 0) + 1;
  try { localStorage.setItem(OPENS, JSON.stringify(o)); } catch { /* a convenience, not a record */ }
}

/**
 * Warm the three reports this person opens most (a sensible three before there
 * is history). A lead workspace has no reports here; it gets the code of its
 * daily pages instead, which used to ship in the main bundle.
 */
const LEAD_CORE = ['/leads', '/contacts', '/whatsapp', '/follow-ups', '/tasks', '/meetings'];
export function warmFavourites(currentPath) {
  if (!isStore()) { LEAD_CORE.forEach(preloadChunk); return; }
  const o = readOpens();
  const [here] = dataFor(currentPath);
  const ranked = Object.keys(o).sort((a, b) => o[b] - o[a]);
  const picks = [...ranked, '/dashboard', '/growth/campaigns', '/commerce']
    .filter((p, i, a) => DATA[p] && p !== here && a.indexOf(p) === i).slice(0, 3);
  for (const p of picks) { preloadChunk(p); prefetchRouteData(p); }
}
